import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "../../../hooks"
import { getSessionToken } from "../../../network"
import { setUsdcBalance } from "../../../stores/NetworkStore"
import { Modal } from "../modal/modal"
import { useWalletBalance } from "./use-wallet-balance"
import "./deposit-modal.css"

const QUICK_AMOUNTS = [5, 10, 25, 50]

interface DepositModalProps {
  show: boolean
  onClose: () => void
}

export function DepositModal({ show, onClose }: DepositModalProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { balance, limits, loading: balanceLoading, fetchBalance } = useWalletBalance()

  const [depositAddress, setDepositAddress] = useState("")
  const [depositAmount, setDepositAmount] = useState("")
  const [creatingCheckout, setCreatingCheckout] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)

  const fetchDepositAddress = useCallback(async () => {
    try {
      const token = getSessionToken()
      const res = await fetch("/wallet/deposit-address", {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setDepositAddress(data.depositAddress)
      }
    } catch (e) {
      console.error("Failed to fetch deposit address:", e)
    }
  }, [])

  useEffect(() => {
    if (show) {
      setError(null)
      setSuccess(null)
      setInitialLoading(true)
      Promise.all([fetchBalance(), fetchDepositAddress()]).finally(() => {
        setInitialLoading(false)
      })

      // Check Stripe redirect params
      const params = new URLSearchParams(window.location.search)
      const stripeDeposit = params.get("stripe_deposit")
      if (stripeDeposit === "success") {
        setSuccess(t("deposit_success_redirect"))
        const url = new URL(window.location.href)
        url.searchParams.delete("stripe_deposit")
        window.history.replaceState({}, "", url.pathname + url.search)
      } else if (stripeDeposit === "cancelled") {
        setError(t("deposit_cancelled"))
        const url = new URL(window.location.href)
        url.searchParams.delete("stripe_deposit")
        window.history.replaceState({}, "", url.pathname + url.search)
      }
    }
  }, [show])

  const handleCardDeposit = async () => {
    const amount = parseFloat(depositAmount)
    if (isNaN(amount) || amount <= 0) {
      setError(t("invalid_amount"))
      return
    }
    if (amount < 1) {
      setError(t("min_deposit_amount", { min: "1" }))
      return
    }
    if (amount > 1000) {
      setError(t("max_deposit_amount", { max: "1,000" }))
      return
    }

    setCreatingCheckout(true)
    setError(null)
    setSuccess(null)

    try {
      const token = getSessionToken()
      const response = await fetch("/wallet/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ amount })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.url) {
          window.location.href = data.url
        }
      } else {
        const err = await response.json()
        setError(err.error || t("checkout_failed"))
      }
    } catch (e) {
      setError(t("checkout_failed"))
    } finally {
      setCreatingCheckout(false)
    }
  }

  const handleRefreshDeposits = async () => {
    setProcessing(true)
    setError(null)
    setSuccess(null)

    try {
      const token = getSessionToken()
      const response = await fetch("/wallet/refresh-deposits", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        dispatch(setUsdcBalance(data.newBalance))
        if (data.depositsFound > 0) {
          setSuccess(t("deposits_found", { count: data.depositsFound, amount: data.totalAmount.toFixed(2) }))
        } else {
          setSuccess(t("no_new_deposits"))
        }
        fetchBalance()
      } else {
        const err = await response.json()
        setError(err.error || t("refresh_failed"))
      }
    } catch (e) {
      setError(t("refresh_failed"))
    } finally {
      setProcessing(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setSuccess(t("copied_to_clipboard"))
    setTimeout(() => setSuccess(null), 2000)
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      header={t("deposit")}
      className="deposit-modal"
      body={
        initialLoading ? (
          <p className="deposit-modal-loading">{t("loading")}</p>
        ) : (
          <div className="deposit-modal-content">
            <div className="deposit-modal-balance my-box">
              <div className="deposit-balance-row">
                <span className="deposit-balance-label">{t("your_balance")}</span>
                <span className="deposit-balance-value">${balance.toFixed(2)} USDC</span>
              </div>
              <button
                className="bubbly green small"
                onClick={fetchBalance}
                disabled={balanceLoading}
              >
                {balanceLoading ? t("processing") : "Refresh"}
              </button>
            </div>

            {error && <div className="wallet-error">{error}</div>}
            {success && <div className="wallet-success">{success}</div>}

            {/* Credit card deposit hidden for now
            <div className="deposit-section">
              <h3>{t("deposit_with_card")}</h3>
              <p className="deposit-limit-info">
                {t("min_deposit_amount", { min: "1" })}
              </p>
              {limits && (
                <p className="deposit-limit-info">
                  {t("deposit_limit_info", {
                    maxPerTx: limits.maxDepositPerTx.toLocaleString(),
                    maxDaily: limits.maxDailyDeposit.toLocaleString()
                  })}
                </p>
              )}
              <div className="quick-amount-buttons">
                {QUICK_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    className={`quick-amount-btn${depositAmount === String(amt) ? " selected" : ""}`}
                    onClick={() => setDepositAmount(String(amt))}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
              <div className="deposit-form-row">
                <input
                  type="number"
                  placeholder={t("enter_amount")}
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  step="1"
                  min="1"
                  max="1000"
                />
                <button
                  className={`bubbly green${creatingCheckout ? " loading" : ""}`}
                  onClick={handleCardDeposit}
                  disabled={creatingCheckout || !depositAmount}
                >
                  {creatingCheckout ? t("creating_checkout") : t("pay_with_card")}
                </button>
              </div>
            </div>
            */}

            <div className="deposit-section">
              <h3>{t("deposit")} (Crypto)</h3>
              <p className="deposit-limit-info">
                {t("min_deposit_amount", { min: "1" })}
              </p>
              {limits && (
                <p className="deposit-limit-info">
                  {t("deposit_limit_info", {
                    maxPerTx: limits.maxDepositPerTx.toLocaleString(),
                    maxDaily: limits.maxDailyDeposit.toLocaleString()
                  })}
                </p>
              )}
              <div className="deposit-address">
                <label>{t("send_usdc_to")}</label>
                <div className="address-display">
                  <code>{depositAddress}</code>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(depositAddress)}
                    title={t("copy")}
                  >
                    Copy
                  </button>
                </div>
              </div>
              <p className="deposit-info">{t("after_sending_refresh")}</p>
              <button
                className={`bubbly green${processing ? " loading" : ""}`}
                onClick={handleRefreshDeposits}
                disabled={processing}
              >
                {processing ? t("processing") : t("refresh_balance")}
              </button>
            </div>
          </div>
        )
      }
    />
  )
}
